import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, Music, Book, MessageSquare, DollarSign } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import Button from '../common/Button';

const PROGRAMME_TYPES = [
  'Culte',
  'Réunion de Prière',
  'Étude Biblique',
  'Événement Spécial',
  'Autre'
];

const PROGRAMME_STATUS = ['À venir', 'En cours', 'Terminé', 'Annulé'];

const ITEM_TYPES = [
  { value: 'lohahevitra', label: 'Lohahevitra', icon: Book },
  { value: 'fiarahabana', label: 'Fiarahabana apostolika', icon: MessageSquare },
  { value: 'salamo', label: 'Salamo', icon: Book },
  { value: 'vavaka', label: 'Vavaka', icon: MessageSquare },
  { value: 'fankalazana', label: 'Fankalazana ny tenin\'Andriamanitra', icon: Book },
  { value: 'dimy_minitra', label: 'Dimy minitra ho an\'ny tanora sy ny ankizy', icon: MessageSquare },
  { value: 'fampianarana', label: 'Fampianarana', icon: Book },
  { value: 'toriteny', label: 'Toriteny', icon: MessageSquare },
  { value: 'hira', label: 'Hira', icon: Music },
  { value: 'hira_stk', label: 'Hira STK', icon: Music },
  { value: 'famelan_keloka', label: 'Famelan-keloka', icon: MessageSquare },
  { value: 'fanekem_pinoana', label: 'Fanekem-pinoana', icon: MessageSquare },
  { value: 'filazan_draharaha', label: 'Filazan-draharaha', icon: MessageSquare },
  { value: 'asa_vavolombelona', label: 'Asa vavolombelona', icon: MessageSquare },
  { value: 'rakitra', label: 'Rakitra', icon: DollarSign },
  { value: 'fanolorana_rakitra', label: 'Fanolorana ny Rakitra', icon: DollarSign },
  { value: 'vavaka_fangatahana', label: 'Vavaka fangatahana', icon: MessageSquare },
  { value: 'fanasan_tompo', label: 'Fanasan\'ny Tompo', icon: MessageSquare },
  { value: 'tsodrano', label: 'Tsodrano', icon: MessageSquare },
  { value: 'trinite', label: 'Trinite', icon: MessageSquare }
];

const ProgrammeEditor = ({ programme, onSave, onCancel }) => {
  const { data } = useData();
  const [form, setForm] = useState({
    title: '',
    date: '',
    time: '10:00',
    endDate: '',
    endTime: '',
    type: 'Culte',
    status: 'À venir',
    items: []
  });
  const [errors, setErrors] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    if (programme) {
      setForm({
        title: programme.title || '',
        date: programme.date || '',
        time: programme.time || '10:00',
        endDate: programme.endDate || '',
        endTime: programme.endTime || '',
        type: programme.type || 'Culte',
        status: programme.status || 'À venir',
        items: programme.items || []
      });
    }
  }, [programme]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const addItem = () => {
    const newItem = {
      id: Date.now(),
      type: 'lohahevitra',
      fihiranaId: null,
      andininy: '', // Champ pour les versets (numéros de strophes)
      title: '',
      leader: '',
      duration: 5,
      durationIndeterminate: false,
      order: form.items.length + 1
    };
    setForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const updateItem = (itemId, field, value) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeItem = (itemId) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetItem.id) return;

    const items = [...form.items];
    const draggedIndex = items.findIndex(item => item.id === draggedItem.id);
    const targetIndex = items.findIndex(item => item.id === targetItem.id);

    items.splice(draggedIndex, 1);
    items.splice(targetIndex, 0, draggedItem);

    // Mettre à jour les ordres
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    setForm(prev => ({ ...prev, items: updatedItems }));
    setDraggedItem(null);
  };

  const validate = () => {
    const newErrors = {};
    if (!form.title.trim()) newErrors.title = 'Le titre est requis';
    if (!form.date) newErrors.date = 'La date est requise';
    if (!form.time) newErrors.time = 'L\'heure est requise';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(form);
    }
  };

  const getTotalDuration = () => {
    const total = form.items.reduce((sum, item) => {
      if (item.durationIndeterminate) return sum;
      return sum + (parseInt(item.duration) || 0);
    }, 0);
    const hasIndeterminate = form.items.some(item => item.durationIndeterminate);
    return hasIndeterminate ? `${total}+ (durée indéterminée)` : total;
  };

  const getItemIcon = (type) => {
    const itemType = ITEM_TYPES.find(t => t.value === type);
    const Icon = itemType?.icon || MessageSquare;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Informations de base */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Titre du programme *
          </label>
          <input
            type="text"
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="Ex: Culte Dimanche 27 Octobre 2025"
            className={`w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.title ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
            }`}
          />
          {errors.title && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.title}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Date de début *
          </label>
          <input
            type="date"
            name="date"
            value={form.date}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.date ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
            }`}
          />
          {errors.date && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.date}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Heure de début *
          </label>
          <input
            type="time"
            name="time"
            value={form.time}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.time ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
            }`}
          />
          {errors.time && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.time}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Date de fin (optionnel)
          </label>
          <input
            type="date"
            name="endDate"
            value={form.endDate}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Heure de fin (optionnel)
          </label>
          <input
            type="time"
            name="endTime"
            value={form.endTime}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Type de programme
          </label>
          <select
            name="type"
            value={form.type}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {PROGRAMME_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Statut
          </label>
          <select
            name="status"
            value={form.status}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {PROGRAMME_STATUS.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Déroulement du programme */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Déroulement du programme</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Durée totale : {getTotalDuration()} minutes
            </p>
          </div>
          <Button
            type="button"
            onClick={addItem}
            icon={Plus}
            size="sm"
          >
            Ajouter un élément
          </Button>
        </div>

        {errors.items && (
          <p className="mb-3 text-sm text-red-600 dark:text-red-400">{errors.items}</p>
        )}

        {form.items.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
            <p className="text-gray-600 dark:text-gray-400">Aucun élément dans le programme</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Cliquez sur "Ajouter un élément" pour commencer</p>
          </div>
        ) : (
          <div className="space-y-3">
            {form.items.map((item, index) => (
              <div
                key={item.id}
                draggable
                onDragStart={(e) => handleDragStart(e, item)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, item)}
                className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 cursor-move"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 pt-2">
                    <GripVertical className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Type d'élément
                        </label>
                        <select
                          value={item.type}
                          onChange={(e) => updateItem(item.id, 'type', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {ITEM_TYPES.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {item.type === 'hira' ? (
                        <>
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Fihirana
                            </label>
                            <select
                              value={item.fihiranaId || ''}
                              onChange={(e) => updateItem(item.id, 'fihiranaId', e.target.value || null)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="">Sélectionner un Fihirana</option>
                              {data.fihirana?.map(f => (
                                <option key={f.id} value={f.id}>
                                  n°{f.number} - {f.title}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Andininy (versets)
                            </label>
                            <input
                              type="text"
                              value={item.andininy || ''}
                              onChange={(e) => updateItem(item.id, 'andininy', e.target.value)}
                              placeholder="Ex: 1, 3, 5 ou 1-3"
                              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </>
                      ) : (
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Titre (optionnel)
                          </label>
                          <input
                            type="text"
                            value={item.title || ''}
                            onChange={(e) => updateItem(item.id, 'title', e.target.value)}
                            placeholder="Ex: Prière d'ouverture"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Animé par (optionnel)
                        </label>
                        <input
                          type="text"
                          value={item.leader || ''}
                          onChange={(e) => updateItem(item.id, 'leader', e.target.value)}
                          placeholder="Ex: Pasteur Rakoto"
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Durée
                        </label>
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <input
                              type="number"
                              value={item.durationIndeterminate ? '' : (item.duration || '')}
                              onChange={(e) => updateItem(item.id, 'duration', parseInt(e.target.value) || 0)}
                              min="1"
                              disabled={item.durationIndeterminate}
                              placeholder="minutes"
                              className={`flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                item.durationIndeterminate ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                            />
                          </div>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={item.durationIndeterminate || false}
                              onChange={(e) => {
                                updateItem(item.id, 'durationIndeterminate', e.target.checked);
                                if (e.target.checked) {
                                  updateItem(item.id, 'duration', 0);
                                }
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-xs text-gray-600 dark:text-gray-400">Durée indéterminée</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="flex-shrink-0 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-2"
                    title="Supprimer"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Boutons d'action */}
      <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Annuler
        </Button>
        <Button type="submit">
          {programme ? 'Mettre à jour' : 'Créer le programme'}
        </Button>
      </div>
    </form>
  );
};

export default ProgrammeEditor;
